import React from "react";
import { BookOpen, Video, Calendar, Download } from "lucide-react";
import { z } from "zod";

const ResourceSchema = z.object({
	type: z.string(),
	title: z.string(),
	description: z.string(),
	icon: z.any(),
	color: z.string(),
});

const ResourcesSchema = z.object({
	header: z.string().default("BIM Resources & Learning"),
	description: z
		.string()
		.default(
			"Stay ahead with our comprehensive collection of BIM tutorials, guides, and industry insights."
		),
	learnMoreText: z.string().default("Learn More"),
	buttonText: z.string().default("Explore All Resources"),
	resources: z.array(ResourceSchema).default([
		{
			type: "Guide",
			title: "Getting to ISO 19650 Compliance",
			description: "Complete guide to implementing ISO 19650 standards in your BIM workflow.",
			icon: BookOpen,
			color: "bg-primary-500",
		},
		{
			type: "Video",
			title: "Advanced Revit Techniques",
			description: "Master advanced modeling techniques and family creation in Revit.",
			icon: Video,
			color: "bg-secondary-500",
		},
		{
			type: "Webinar",
			title: "BIM Coordination Best Practices",
			description: "Live session on effective coordination strategies for large projects.",
			icon: Calendar,
			color: "bg-accent-500",
		},
		{
			type: "Template",
			title: "BIM Execution Plan Template",
			description: "Ready-to-use BEP template for your next project.",
			icon: Download,
			color: "bg-green-500",
		},
	]),
});

type ResourcesProps = z.input<typeof ResourcesSchema>;

const Resources: React.FC<ResourcesProps> = (props = {}) => {
	const data = ResourcesSchema.parse(props);

	return (
		<section id="resources" className="bg-gray-50 py-20">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="mb-12 text-center">
					<h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">{data.header}</h2>
					<p className="mx-auto max-w-3xl text-xl text-gray-600">{data.description}</p>
				</div>

				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
					{data.resources.map((resource, index) => (
						<div
							key={index}
							className="group rounded-xl bg-white p-6 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
						>
							<div
								className={`h-12 w-12 ${resource.color} mb-4 flex items-center justify-center rounded-lg transition-transform group-hover:scale-110`}
							>
								<resource.icon className="h-6 w-6 text-white" />
							</div>

							<div className="mb-2">
								<span className="text-sm font-semibold uppercase tracking-wide text-primary-500">
									{resource.type}
								</span>
							</div>

							<h3 className="mb-2 text-lg font-bold text-gray-900 transition-colors group-hover:text-primary-500">
								{resource.title}
							</h3>

							<p className="mb-4 text-sm leading-relaxed text-gray-600">{resource.description}</p>

							<button className="flex items-center space-x-1 text-sm font-semibold text-primary-500 transition-colors hover:text-primary-600">
								<span>{data.learnMoreText}</span>
								<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 5l7 7-7 7"
									/>
								</svg>
							</button>
						</div>
					))}
				</div>

				<div className="mt-12 text-center">
					<button className="rounded-lg bg-gray-100 px-8 py-3 font-semibold text-gray-800 transition-colors hover:bg-gray-200">
						{data.buttonText}
					</button>
				</div>
			</div>
		</section>
	);
};

export default Resources;
